import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CheckCircle2, X, Languages, ShieldCheck, Loader2, CloudOff, RotateCw, AlertTriangle, PenLine } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

const blobToDataURL = (blob) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob);
});
const dataURLToBlob = async (d) => (await fetch(d)).blob();

// Signature canvas — reports every completed stroke on pen-up, and reports null on erase.
const SignaturePad = ({ onCapture, onErase }) => {
  const ref = useRef(null); const drawing = useRef(false); const dirty = useRef(false);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
  const start = (e) => { drawing.current = true; const c = ref.current.getContext('2d'); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); dirty.current = true; const c = ref.current.getContext('2d'); const p = pos(e); c.lineWidth = 2; c.lineCap = 'round'; c.strokeStyle = '#0E2A47'; c.lineTo(p.x, p.y); c.stroke(); };
  const end = () => { if (drawing.current && dirty.current) { ref.current.toBlob((b) => b && onCapture(b), 'image/png'); } drawing.current = false; };
  const erase = () => { const c = ref.current.getContext('2d'); c.clearRect(0, 0, ref.current.width, ref.current.height); dirty.current = false; onErase && onErase(); };
  return (
    <div>
      <canvas ref={ref} width={520} height={150} onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full touch-none rounded-lg border border-slate-300 bg-white" data-testid="syn-cr-signature-canvas" />
      <div className="mt-1 flex items-center justify-between">
        <p className="text-[11px] text-slate-400">Sign above with your finger or a stylus.</p>
        <button type="button" onClick={erase} className="text-[11px] text-slate-500 hover:underline" data-testid="syn-cr-signature-erase">Continue Signing (erase)</button>
      </div>
    </div>
  );
};

const STATUS_TEXT = {
  uploading: 'Saving signature…',
  saved_pending: 'Signature saved — confirmation required',
  local_only: 'Saved on this device — upload pending',
  upload_failed: 'Signature could not upload — Retry',
  confirmed: 'Signature confirmed and saved',
  declined: 'Representative declined to sign',
};

const SynovusClientReview = ({ inspectionId, onClose, onUpdated }) => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [lang, setLang] = useState('en');
  const [status, setStatus] = useState('idle');   // idle | uploading | saved_pending | local_only | upload_failed | confirmed | declined
  const [hasDrawing, setHasDrawing] = useState(false);
  const [busy, setBusy] = useState(false);
  const seqRef = useRef(0);        // monotonic local sequence — newest stroke wins
  const blobRef = useRef(null);    // latest drawn blob (for confirm / retry)
  const LKEY = `synsig:${inspectionId}`;

  const saveLocal = useCallback(async (blob, seq) => {
    try { const d = await blobToDataURL(blob); localStorage.setItem(LKEY, JSON.stringify({ dataUrl: d, seq, ts: Date.now() })); } catch (e) { /* storage full — server copy still attempted */ }
  }, [LKEY]);
  const loadLocal = useCallback(() => { try { return JSON.parse(localStorage.getItem(LKEY) || 'null'); } catch { return null; } }, [LKEY]);
  const clearLocal = useCallback(() => { try { localStorage.removeItem(LKEY); } catch { /* noop */ } }, [LKEY]);

  // ---- server pending auto-save (ordering-guarded) ----
  const uploadPending = useCallback(async (blob, seq) => {
    setStatus('uploading');
    try {
      const fd = new FormData(); fd.append('file', blob, 'signature.png'); fd.append('seq', String(seq));
      await axios.post(`${API}/operations/synovus/inspections/${inspectionId}/signature/pending`, fd, auth(token));
      if (seq >= seqRef.current) setStatus('saved_pending');   // ignore stale responses
    } catch (e) {
      const stale = e?.response?.status === 409;
      if (stale) return; // a newer stroke superseded this one — keep the newer state
      if (seq >= seqRef.current) setStatus(e?.response ? 'upload_failed' : 'local_only');
    }
  }, [API, inspectionId, token]);

  const onCapture = useCallback(async (blob) => {
    const seq = ++seqRef.current;
    blobRef.current = blob;
    setHasDrawing(true);
    await saveLocal(blob, seq);   // durable device copy FIRST — never lost
    uploadPending(blob, seq);     // then server (best-effort)
  }, [saveLocal, uploadPending]);

  const onErase = useCallback(() => { blobRef.current = null; setHasDrawing(false); }, []);

  const retry = useCallback(async () => {
    const local = loadLocal();
    const blob = blobRef.current || (local ? await dataURLToBlob(local.dataUrl) : null);
    const seq = local?.seq || seqRef.current;
    if (!blob) { toast.error('Nothing to retry — please sign first'); return; }
    seqRef.current = Math.max(seqRef.current, seq);
    uploadPending(blob, seq);
  }, [loadLocal, uploadPending]);

  const load = useCallback(async (l = lang) => {
    const r = await axios.get(`${API}/operations/synovus/inspections/${inspectionId}/client-review?lang=${l}`, auth(token));
    setData(r.data);
    const rep = r.data.representative || {};
    seqRef.current = Math.max(seqRef.current, rep.signature_seq || 0);
    if (rep.acknowledged && rep.has_signature) { setStatus('confirmed'); setHasDrawing(false); clearLocal(); return r.data; }
    if (rep.declined) { setStatus('declined'); return r.data; }
    if (rep.has_pending) { setStatus('saved_pending'); return r.data; }
    // Recover an unsynced device draft captured before a crash/offline close.
    const local = loadLocal();
    if (local) { const seq = Math.max(seqRef.current, local.seq || 0); seqRef.current = seq; const blob = await dataURLToBlob(local.dataUrl); blobRef.current = blob; setHasDrawing(true); uploadPending(blob, seq); }
    else setStatus('idle');
    return r.data;
  }, [API, inspectionId, token, lang, clearLocal, loadLocal, uploadPending]);

  useEffect(() => { load('en'); /* eslint-disable-next-line */ }, [inspectionId]);

  const switchLang = async (l) => { setLang(l); await load(l); };

  const rep = data?.representative || {};
  const cf = data?.client_feedback;
  const unconfirmed = ['saved_pending', 'local_only', 'upload_failed', 'uploading'].includes(status) || hasDrawing;

  // Warn on refresh / tab close while a signature is unconfirmed (durable recovery still applies).
  useEffect(() => {
    if (!unconfirmed) return;
    const h = (e) => { e.preventDefault(); e.returnValue = 'Your signature has not been confirmed.'; return e.returnValue; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [unconfirmed]);

  const attemptClose = () => {
    if (status !== 'confirmed' && status !== 'declined' && unconfirmed) {
      const msg = (status === 'local_only' || status === 'upload_failed')
        ? 'Your signature has not been confirmed and is still saved only on this device (upload pending). Please Confirm & Save Signature or Continue Signing. Close anyway?'
        : 'Your signature has not been confirmed. Please Confirm & Save Signature or Continue Signing. Close anyway?';
      if (!window.confirm(msg)) return;
    }
    onClose && onClose();
  };

  const confirmSignature = async () => {
    const haveSomething = hasDrawing || blobRef.current || rep.has_pending || loadLocal();
    if (!haveSomething) { toast.error('Please sign in the box first'); return; }
    setBusy(true); setStatus('uploading');
    try {
      const fd = new FormData();
      const local = loadLocal();
      const blob = blobRef.current || (local ? await dataURLToBlob(local.dataUrl) : null);
      if (blob) fd.append('file', blob, 'signature.png');   // else server promotes the pending copy
      fd.append('seq', String(seqRef.current));
      await axios.post(`${API}/operations/synovus/inspections/${inspectionId}/signature/confirm`, fd, auth(token));
      clearLocal(); blobRef.current = null; setHasDrawing(false);
      setStatus('confirmed');
      toast.success('Signature confirmed and saved');
      await load();
      onUpdated && onUpdated();
    } catch (e) {
      setStatus(blobRef.current || loadLocal() ? 'local_only' : 'upload_failed');
      toast.error(e?.response?.data?.detail || 'Could not confirm signature — your signature is still saved, please Retry');
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!window.confirm('Record that the representative was present but DECLINED to sign? Any captured signature will be archived and removed. This is an explicit action.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/operations/synovus/inspections/${inspectionId}/rep-decline`, {}, auth(token));
      clearLocal(); blobRef.current = null; setHasDrawing(false);
      toast.success('Recorded: representative declined to sign');
      await load();
      onUpdated && onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record'); }
    finally { setBusy(false); }
  };

  const clearSignature = async () => {
    if (!window.confirm('Clear the saved signature and sign again? The previous signature is archived for internal records. This is an explicit action.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/operations/synovus/inspections/${inspectionId}/signature/clear`, {}, auth(token));
      clearLocal(); blobRef.current = null; setHasDrawing(false); setStatus('idle');
      await load();
      onUpdated && onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear'); }
    finally { setBusy(false); }
  };

  const StatusLine = () => {
    if (status === 'idle') return null;
    const icon = status === 'uploading' ? <Loader2 size={13} className="animate-spin" />
      : status === 'saved_pending' ? <CheckCircle2 size={13} />
      : status === 'confirmed' ? <CheckCircle2 size={13} />
      : status === 'local_only' ? <CloudOff size={13} />
      : status === 'upload_failed' ? <AlertTriangle size={13} />
      : status === 'declined' ? <X size={13} /> : null;
    const tone = status === 'confirmed' ? 'text-[#4d7c0f]'
      : status === 'upload_failed' ? 'text-red-600'
      : status === 'local_only' ? 'text-amber-600'
      : status === 'declined' ? 'text-slate-500' : 'text-[#0E2A47]';
    return (
      <div className={`flex items-center gap-1.5 text-xs font-semibold ${tone}`} data-testid="syn-cr-sig-status" data-status={status}>
        {icon} {STATUS_TEXT[status]}
        {(status === 'local_only' || status === 'upload_failed') && (
          <button type="button" onClick={retry} disabled={busy} data-testid="syn-cr-sig-retry"
            className="ml-1 inline-flex items-center gap-1 rounded border border-current px-1.5 py-0.5 text-[11px] hover:bg-slate-50"><RotateCw size={11} /> Retry</button>
        )}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) attemptClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="syn-client-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#4d7c0f]" /> Client / Branch Review</DialogTitle>
          <DialogDescription>Client-facing inspection findings only. Please review and acknowledge below.</DialogDescription>
        </DialogHeader>

        {!data ? <p className="py-8 text-center text-sm text-slate-400" data-testid="syn-cr-loading">Preparing the review…</p> : (
          <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1">
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-600" data-testid="syn-cr-header">
                <div className="font-bold text-[#0E2A47]">{data.customer_name} — {data.location_name}</div>
                <div>{data.inspection_number} · {data.inspection_date} · Inspector: {data.inspector_name || '—'}</div>
              </div>
              <div className="flex items-center gap-1">
                {['en', 'es'].map((l) => (
                  <button key={l} onClick={() => switchLang(l)} data-testid={`syn-cr-lang-${l}`}
                    className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-bold ${lang === l ? 'bg-[#0E2A47] text-white' : 'border border-slate-300 bg-white text-slate-500'}`}>
                    {l === 'es' && <Languages size={11} />}{l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {(data.sections || []).map((s) => (
              <div key={s.key} className="rounded-xl border border-slate-200 p-3" data-testid={`syn-cr-section-${s.key}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-[#0E2A47]">{s.title}</div>
                  <div className="rounded bg-[#8FC93A]/15 px-2 py-0.5 text-xs font-bold text-[#4d7c0f]">{s.rating_label}: {s.rating_text}</div>
                </div>
                {s.client_note && <p className="mt-1 text-sm text-slate-600"><span className="font-semibold">{s.client_note_label}:</span> {s.client_note}</p>}
                {(s.photos || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid={`syn-cr-photos-${s.key}`}>
                    {s.photos.map((p) => (
                      <img key={p.id} src={`${API}/operations/synovus/inspections/${inspectionId}/photo/${p.id}/raw?auth=${token}`}
                        alt={p.caption || 'photo'} onClick={() => window.open(`${API}/operations/synovus/inspections/${inspectionId}/photo/${p.id}/raw?auth=${token}`, '_blank')}
                        className="h-20 w-24 cursor-pointer rounded border border-slate-200 object-cover" />
                    ))}
                  </div>
                )}
                {(s.deficiencies || []).map((d, i) => (
                  <div key={i} className="mt-2 rounded-lg bg-slate-50 p-2 text-sm" data-testid={`syn-cr-def-${s.key}-${i}`}>
                    <div className="text-slate-700"><span className="font-semibold">{s.deficiency_label}:</span> {d.description}</div>
                    {d.action_number && <div className="text-[11px] text-slate-500">{s.linked_action_label} {d.action_number} · {d.status}</div>}
                    {d.before_photo_id && (
                      <img src={`${API}/operations/synovus/inspections/${inspectionId}/photo/${d.before_photo_id}/raw?auth=${token}`}
                        alt={s.before_label} onClick={() => window.open(`${API}/operations/synovus/inspections/${inspectionId}/photo/${d.before_photo_id}/raw?auth=${token}`, '_blank')}
                        className="mt-1 h-20 w-24 cursor-pointer rounded border border-slate-200 object-cover" />
                    )}
                  </div>
                ))}
              </div>
            ))}

            {cf && (
              <div className="rounded-xl border border-sky-200 bg-sky-50 p-3" data-testid="syn-cr-feedback">
                <div className="font-semibold text-[#0E2A47]">{cf.title}</div>
                <p className="mt-0.5 text-[11px] text-sky-800">{cf.intro}</p>
                {cf.feedback_provided ? (
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    {cf.rep_name && <div>{cf.rep_name}{cf.rep_role ? ` · ${cf.role_label}: ${cf.rep_role}` : ''}</div>}
                    {cf.perception_rating && <div><span className="font-semibold">{cf.perception_label}:</span> {cf.perception_rating}</div>}
                    {cf.comments && <div><span className="font-semibold">{cf.comments_label}:</span> {cf.comments}</div>}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">{cf.none_text}</p>
                )}
              </div>
            )}

            {/* Acknowledgment + signature */}
            <div className="rounded-xl border border-[#8FC93A]/50 bg-[#8FC93A]/5 p-3" data-testid="syn-cr-ack">
              <p className="text-sm text-slate-700" data-testid="syn-cr-ack-text">{data.acknowledgment_text}</p>

              {status === 'confirmed' ? (
                <div className="mt-3" data-testid="syn-cr-signed">
                  <p className="flex items-center gap-1 text-sm font-semibold text-[#4d7c0f]"><CheckCircle2 size={15} /> Signature confirmed and saved{rep.name ? ` — ${rep.name}` : ''}</p>
                  <img src={`${API}/operations/synovus/inspections/${inspectionId}/signature/raw?auth=${token}&t=${Date.now()}`} alt="signature"
                    className="mt-1 h-20 rounded border border-slate-200 bg-white" data-testid="syn-cr-signature-preview" />
                  <button type="button" onClick={clearSignature} disabled={busy} data-testid="syn-cr-sig-clear"
                    className="mt-2 inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><PenLine size={12} /> Clear &amp; sign again</button>
                </div>
              ) : status === 'declined' ? (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-semibold text-slate-500" data-testid="syn-cr-declined">Representative was present but declined to sign.</p>
                  <button type="button" onClick={clearSignature} disabled={busy} data-testid="syn-cr-sign-instead"
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><PenLine size={12} /> Sign instead</button>
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Signature{rep.name ? ` — ${rep.name}${rep.title ? `, ${rep.title}` : ''}` : ''}
                  </div>
                  {(status === 'saved_pending' || status === 'local_only' || status === 'upload_failed' || status === 'uploading') && (rep.has_pending || hasDrawing) && (
                    <img src={rep.has_pending
                      ? `${API}/operations/synovus/inspections/${inspectionId}/signature/pending/raw?auth=${token}&t=${Date.now()}`
                      : (loadLocal()?.dataUrl || '')}
                      alt="pending signature" className="h-16 rounded border border-dashed border-amber-300 bg-white" data-testid="syn-cr-pending-preview" />
                  )}
                  <SignaturePad onCapture={onCapture} onErase={onErase} />
                  <StatusLine />
                  <p className="text-[11px] text-slate-500">By tapping <span className="font-semibold">Confirm &amp; Save Signature</span> you confirm you have reviewed the findings above.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={confirmSignature} disabled={busy || (!hasDrawing && !rep.has_pending && !loadLocal())} data-testid="syn-cr-sign-confirm"
                      className="rounded-lg bg-[#0E2A47] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0E2A47]/90 disabled:opacity-50">Confirm &amp; Save Signature</button>
                    <button type="button" onClick={decline} disabled={busy} data-testid="syn-cr-decline"
                      className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Decline to sign</button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={attemptClose} data-testid="syn-cr-close"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"><X size={14} /> Close review</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SynovusClientReview;
